import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
	ArrowDown,
	ArrowLeft,
	ArrowUp,
	ChevronLeft,
	ChevronRight,
	Edit3,
	ImageIcon,
	Plus,
	Search,
	X,
} from 'lucide-react'
import { FormEvent, useEffect, useMemo, useRef, useState } from 'react'
import { Route, Switch, useHistory, useLocation, useParams } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { useAdminApi } from '@app/api/api-client-context'
import { useAuth } from '@app/auth/auth-context'
import {
	createInitialProgramMutation,
	getProgramStructure,
	programKeys,
	programLifecycleClass,
	programLifecycleLabel,
} from '@entities/program'
import type { MediaAsset } from '@entities/media'
import { createIdempotencyKey, getNextCursor, unwrapApiResponse } from '@shared/api'
import { getInterfaceErrorDescription } from '@shared/i18n/interface-messages'
import { EmptyState, ErrorState, LoadingState, PageHeader } from '@shared/ui/page'
import { useToast } from '@shared/ui/toast'
import {
	parseProgramCatalogFilters,
	serializeProgramCatalogFilters,
	type ProgramCatalogFilters,
} from '../model/catalog-filters'
import { ProgramBuilder } from './builder'

const formatDate = (value?: string) =>
	value
		? new Intl.DateTimeFormat('ru-RU', {
				dateStyle: 'medium',
				timeStyle: 'short',
			}).format(new Date(value))
		: '—'

const getMediaPreview = (asset?: MediaAsset) =>
	asset?.variants?.find(variant => variant.role === 'main')?.url ?? asset?.variants?.[0]?.url

const ProgramCover = ({ assetId, size = 'catalog' }: { assetId?: string; size?: 'catalog' | 'detail' }) => {
	const api = useAdminApi()
	const query = useQuery({
		queryKey: programKeys.cover(assetId ?? 'none'),
		queryFn: async () =>
			unwrapApiResponse(
				await api.GET('/admin/v1/media/{asset_id}', {
					params: { path: { asset_id: assetId ?? '' } },
				}),
			).data,
		enabled: Boolean(assetId),
		staleTime: 60_000,
	})
	const preview = getMediaPreview(query.data)
	const className = size === 'detail' ? 'h-44 w-full rounded-xl' : 'h-12 w-12 rounded-lg'
	if (preview) {
		return <img src={preview} alt='' className={`${className} object-cover`} />
	}
	return (
		<div className={`flex ${className} items-center justify-center bg-primary/10 text-primary`}>
			<ImageIcon className={size === 'detail' ? 'h-9 w-9' : 'h-5 w-5'} aria-hidden='true' />
		</div>
	)
}

const CreateProgramDialog = ({
	open,
	onOpenChange,
}: {
	open: boolean
	onOpenChange: (open: boolean) => void
}) => {
	const api = useAdminApi()
	const history = useHistory()
	const location = useLocation()
	const queryClient = useQueryClient()
	const { notify } = useToast()
	const [name, setName] = useState('')
	const [description, setDescription] = useState('')
	const attemptKey = useRef(createIdempotencyKey())

	useEffect(() => {
		if (!open) return
		attemptKey.current = createIdempotencyKey()
		setName('')
		setDescription('')
	}, [open])

	const mutation = useMutation({
		mutationFn: async () =>
			unwrapApiResponse(
				await api.POST('/admin/v1/programs', {
					params: { header: { 'Idempotency-Key': attemptKey.current } },
					body: createInitialProgramMutation(name, description),
				}),
			).data,
		onSuccess: async program => {
			await queryClient.invalidateQueries({ queryKey: programKeys.all })
			notify({ title: 'Программа создана', description: program.name, tone: 'success' })
			onOpenChange(false)
			history.push(`/programs/${program.id}${location.search}`)
		},
		onError: error => {
			notify({
				title: 'Не удалось создать программу',
				description: getInterfaceErrorDescription(error),
				tone: 'error',
			})
		},
	})

	const submit = (event: FormEvent) => {
		event.preventDefault()
		if (!name.trim() || mutation.isPending) return
		mutation.mutate()
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<form onSubmit={submit} className='space-y-5'>
					<DialogHeader>
						<DialogTitle>Новая программа</DialogTitle>
						<DialogDescription>
							Будет создан черновик с первой неделей и выходным днём.
						</DialogDescription>
					</DialogHeader>
					<div className='space-y-4'>
						<label className='block space-y-2'>
							<span className='text-sm font-medium'>Название</span>
							<Input
								value={name}
								onChange={event => setName(event.target.value)}
								placeholder='Например, Силовая база'
								autoFocus
								required
							/>
						</label>
						<label className='block space-y-2'>
							<span className='text-sm font-medium'>Описание</span>
							<textarea
								value={description}
								onChange={event => setDescription(event.target.value)}
								placeholder='Цель, аудитория и особенности программы'
								rows={4}
								className='w-full rounded-md border bg-background px-3 py-2 text-sm outline-none ring-offset-background focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
							/>
						</label>
					</div>
					<DialogFooter>
						<Button type='button' variant='outline' onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
							Отмена
						</Button>
						<Button type='submit' disabled={!name.trim() || mutation.isPending}>
							{mutation.isPending ? 'Создание…' : 'Создать программу'}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	)
}

const ProgramCatalog = () => {
	const api = useAdminApi()
	const { can } = useAuth()
	const location = useLocation()
	const history = useHistory()
	const filters = useMemo(() => parseProgramCatalogFilters(location.search), [location.search])
	const [searchDraft, setSearchDraft] = useState(filters.search)
	const [cursor, setCursor] = useState<string | null>(null)
	const [cursorHistory, setCursorHistory] = useState<Array<string | null>>([])
	const [createOpen, setCreateOpen] = useState(false)

	useEffect(() => {
		setSearchDraft(filters.search)
		setCursor(null)
		setCursorHistory([])
	}, [
		filters.search,
		filters.lifecycle,
		filters.sort,
		filters.direction,
		filters.limit,
	])

	const queryFilters = useMemo(() => ({ ...filters, cursor }), [filters, cursor])
	const query = useQuery({
		queryKey: programKeys.list(queryFilters),
		queryFn: async () =>
			unwrapApiResponse(
				await api.GET('/admin/v1/programs', {
					params: {
						query: {
							cursor: cursor ?? undefined,
							limit: filters.limit,
							q: filters.search || undefined,
							lifecycle: filters.lifecycle || undefined,
							sort: filters.sort,
							direction: filters.direction,
						},
					},
				}),
			).data,
		placeholderData: previous => previous,
	})

	const updateFilters = (patch: Partial<ProgramCatalogFilters>) => {
		const next = { ...filters, ...patch }
		history.replace({ pathname: location.pathname, search: serializeProgramCatalogFilters(next) })
	}
	const submitSearch = (event: FormEvent) => {
		event.preventDefault()
		updateFilters({ search: searchDraft })
	}
	const resetFilters = () => {
		setSearchDraft('')
		history.replace({ pathname: location.pathname, search: '' })
	}
	const goNext = () => {
		const next = getNextCursor(query.data?.page)
		if (!next) return
		setCursorHistory(current => [...current, cursor])
		setCursor(next)
	}
	const goPrevious = () => {
		setCursorHistory(current => {
			if (!current.length) return current
			setCursor(current[current.length - 1])
			return current.slice(0, -1)
		})
	}
	const toggleSort = (sort: ProgramCatalogFilters['sort']) => {
		if (filters.sort === sort) {
			updateFilters({ direction: filters.direction === 'asc' ? 'desc' : 'asc' })
		} else {
			updateFilters({ sort, direction: sort === 'name' ? 'asc' : 'desc' })
		}
	}
	const SortIcon = filters.direction === 'asc' ? ArrowUp : ArrowDown
	const hasFilters = Boolean(filters.search || filters.lifecycle)

	return (
		<section className='space-y-6'>
			<PageHeader
				eyebrow='Управление программами'
				title='Программы'
				description='Каталог тренировочных программ, их структура, статусы публикации и версия провайдера.'
				actions={
					can('createProgram') ? (
						<Button onClick={() => setCreateOpen(true)}>
							<Plus className='h-4 w-4' /> Создать программу
						</Button>
					) : undefined
				}
			/>

			<Card>
				<CardContent className='space-y-4 p-4 sm:p-5'>
					<form onSubmit={submitSearch} className='flex flex-col gap-2 sm:flex-row'>
						<div className='relative flex-1'>
							<Search className='pointer-events-none absolute left-3 top-3 h-4 w-4 text-muted-foreground' />
							<Input
								aria-label='Поиск программ'
								value={searchDraft}
								onChange={event => setSearchDraft(event.target.value)}
								placeholder='Поиск по названию и описанию'
								className='pl-9'
							/>
						</div>
						<Button type='submit' variant='outline'>Найти</Button>
						{hasFilters ? (
							<Button type='button' variant='ghost' onClick={resetFilters}>
								<X className='h-4 w-4' /> Сбросить
							</Button>
						) : null}
					</form>
					<div className='grid gap-3 sm:grid-cols-2'>
						<select
							value={filters.lifecycle}
							onChange={event =>
								updateFilters({ lifecycle: event.target.value as ProgramCatalogFilters['lifecycle'] })
							}
							className='h-10 rounded-md border bg-background px-3 text-sm'
							aria-label='Статус программы'>
							<option value=''>Все статусы</option>
							<option value='DRAFT'>Черновики</option>
							<option value='PUBLISHED'>Опубликованные</option>
							<option value='ARCHIVED'>Архивные</option>
						</select>
						<select
							value={filters.limit}
							onChange={event => updateFilters({ limit: Number(event.target.value) })}
							className='h-10 rounded-md border bg-background px-3 text-sm'
							aria-label='Размер страницы'>
							{[10, 25, 50, 100].map(limit => (
								<option key={limit} value={limit}>{limit} на странице</option>
							))}
						</select>
					</div>
				</CardContent>
			</Card>

			{query.isPending ? <LoadingState label='Загрузка каталога программ…' /> : null}
			{query.isError ? <ErrorState error={query.error} onRetry={() => void query.refetch()} /> : null}
			{query.data && query.data.items.length === 0 ? (
				<EmptyState
					title='Программы не найдены'
					description={
						hasFilters
							? 'Измените параметры поиска или сбросьте фильтры.'
							: 'Создайте первую тренировочную программу.'
					}
					action={
						hasFilters ? (
							<Button variant='outline' onClick={resetFilters}>Сбросить фильтры</Button>
						) : can('createProgram') ? (
							<Button onClick={() => setCreateOpen(true)}>Создать программу</Button>
						) : undefined
					}
				/>
			) : null}
			{query.data && query.data.items.length > 0 ? (
				<Card className='overflow-hidden'>
					<div className='overflow-x-auto'>
						<table className='w-full min-w-[900px] text-left text-sm'>
							<caption className='sr-only'>Каталог программ</caption>
							<thead className='border-b bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground'>
								<tr>
									<th scope='col' className='px-4 py-3' aria-sort={filters.sort === 'name' ? (filters.direction === 'asc' ? 'ascending' : 'descending') : 'none'}>
										<button type='button' onClick={() => toggleSort('name')} className='inline-flex items-center gap-1'>
											Программа {filters.sort === 'name' ? <SortIcon className='h-3 w-3' aria-hidden='true' /> : null}
										</button>
									</th>
									<th scope='col' className='px-4 py-3' aria-sort={filters.sort === 'lifecycle' ? (filters.direction === 'asc' ? 'ascending' : 'descending') : 'none'}>
										<button type='button' onClick={() => toggleSort('lifecycle')} className='inline-flex items-center gap-1'>
											Статус {filters.sort === 'lifecycle' ? <SortIcon className='h-3 w-3' aria-hidden='true' /> : null}
										</button>
									</th>
									<th scope='col' className='px-4 py-3'>Структура</th>
									<th scope='col' className='px-4 py-3' aria-sort={filters.sort === 'updated_at' ? (filters.direction === 'asc' ? 'ascending' : 'descending') : 'none'}>
										<button type='button' onClick={() => toggleSort('updated_at')} className='inline-flex items-center gap-1'>
											Изменено {filters.sort === 'updated_at' ? <SortIcon className='h-3 w-3' aria-hidden='true' /> : null}
										</button>
									</th>
									<th scope='col' className='px-4 py-3 text-right'>Действия</th>
								</tr>
							</thead>
							<tbody className='divide-y'>
								{query.data.items.map(program => {
									const structure = getProgramStructure(program)
									return (
										<tr key={program.id} className='hover:bg-muted/30'>
											<td className='px-4 py-3'>
												<div className='flex items-center gap-3'>
													<ProgramCover assetId={program.cover_asset_id} />
													<div className='min-w-0'>
														<p className='max-w-xs truncate font-medium'>{program.name}</p>
														<p className='text-xs text-muted-foreground'>ID {program.id} · v{program.version}</p>
													</div>
												</div>
											</td>
											<td className='px-4 py-3'>
												<span className={`rounded-full px-2.5 py-1 text-xs font-medium ${programLifecycleClass[program.lifecycle]}`}>
													{programLifecycleLabel[program.lifecycle]}
												</span>
											</td>
											<td className='px-4 py-3 text-muted-foreground'>
												{structure.weeks} нед. · {structure.trainingDays} трен. · {structure.restDays} отдых
											</td>
											<td className='px-4 py-3 text-muted-foreground'>{formatDate(program.updated_at)}</td>
											<td className='px-4 py-3 text-right'>
												<Button
													variant='ghost'
													size='sm'
													onClick={() => history.push(`/programs/${program.id}${location.search}`)}>
													<Edit3 className='h-4 w-4' /> Открыть
												</Button>
											</td>
										</tr>
									)
								})}
							</tbody>
						</table>
					</div>
					<div className='flex flex-col gap-3 border-t px-4 py-3 sm:flex-row sm:items-center sm:justify-between'>
						<p className='text-sm text-muted-foreground' role='status' aria-live='polite'>
							Показано {query.data.items.length} из {query.data.page.total}
						</p>
						<div className='flex gap-2'>
							<Button
								variant='outline'
								size='sm'
								disabled={cursorHistory.length === 0 || query.isFetching}
								onClick={goPrevious}>
								<ChevronLeft className='h-4 w-4' /> Назад
							</Button>
							<Button
								variant='outline'
								size='sm'
								disabled={!getNextCursor(query.data.page) || query.isFetching}
								onClick={goNext}>
								Далее <ChevronRight className='h-4 w-4' />
							</Button>
						</div>
					</div>
				</Card>
			) : null}

			<CreateProgramDialog open={createOpen} onOpenChange={setCreateOpen} />
		</section>
	)
}

const ProgramDetail = () => {
	const api = useAdminApi()
	const history = useHistory()
	const location = useLocation()
	const { programId } = useParams<{ programId: string }>()
	const query = useQuery({
		queryKey: programKeys.detail(programId),
		queryFn: async () =>
			unwrapApiResponse(
				await api.GET('/admin/v1/programs/{program_id}', {
					params: { path: { program_id: programId } },
				}),
			).data,
	})

	if (query.isPending) return <LoadingState label='Загрузка программы…' />
	if (query.isError) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />
	const program = query.data

	return (
		<section className='space-y-6'>
			<PageHeader
				eyebrow='Конструктор программы'
				title={program.name}
				description='Структура недель, дней и упражнений сохраняется единым каноническим агрегатом.'
				actions={
					<Button variant='outline' onClick={() => history.push(`/programs${location.search}`)}>
						<ArrowLeft className='h-4 w-4' /> К каталогу
					</Button>
				}
			/>
			<ProgramBuilder program={program} onBack={() => history.push(`/programs${location.search}`)} />
		</section>
	)
}

export const ProgramsPage = () => (
	<Switch>
		<Route exact path='/programs'>
			<ProgramCatalog />
		</Route>
		<Route path='/programs/:programId'>
			<ProgramDetail />
		</Route>
	</Switch>
)
